import {
  AppointmentReducerState,
  AppointmentTypeSelection,
  AppointmentTypeSelectionNullObject,
  Option,
} from '@lichtblick/types';

export enum AppointmentActions {
  SET_WORK_ORDER = 'SET_WORK_ORDER',
  SET_APPOINTMENT = 'SET_APPOINTMENT',
  SET_BRANCH = 'SET_BRANCH',
  SET_PARTNER = 'SET_PARTNER',
  SET_TEAMS = 'SET_TEAMS',
  SET_PEOPLE = 'SET_PEOPLE',
  SET_TYPE = 'SET_TYPE',
  SET_START_DATE = 'SET_START_DATE',
  SET_END_DATE = 'SET_END_DATE',
  SET_FROM_TIME = 'SET_FROM_TIME',
  SET_TO_TIME = 'SET_TO_TIME',
  SET_NOTE_TEXT = 'SET_NOTE_TEXT',
  SET_IS_BINDING = 'SET_IS_BINDING',
  SET_CREATE_NEW_WORKFLOW_INSTANCE = 'SET_CREATE_NEW_WORKFLOW_INSTANCE',
  RESET = 'RESET',
}

export const appointmentDefaultState: AppointmentReducerState = {
  branch: -1,
  teams: [],
  partner: -1,
  people: [],
  appointmentType: AppointmentTypeSelectionNullObject,
  startDate: new Date(new Date().setHours(8, 0, 0, 0)),
  endDate: new Date(new Date().setHours(17, 0, 0, 0)),
  note: '',
  isBinding: false,
  createNewWorkflowInstance: false,
  workOrder: -1,
};

export type AppointmentReducerAction =
  | { payload: AppointmentReducerState; type: AppointmentActions.SET_APPOINTMENT }
  | { payload: number; type: AppointmentActions.SET_WORK_ORDER }
  | {
      payload: number;
      type: AppointmentActions.SET_BRANCH | AppointmentActions.SET_PARTNER;
    }
  | {
      payload: AppointmentTypeSelection | null | undefined;
      type: AppointmentActions.SET_TYPE;
    }
  | {
      payload: Option[];
      type: AppointmentActions.SET_TEAMS | AppointmentActions.SET_PEOPLE;
    }
  | { payload: Date | undefined; type: AppointmentActions.SET_START_DATE }
  | { payload: Date | undefined; type: AppointmentActions.SET_END_DATE }
  | { payload: string | null; type: AppointmentActions.SET_FROM_TIME }
  | { payload: string | null; type: AppointmentActions.SET_TO_TIME }
  | { payload: string; type: AppointmentActions.SET_NOTE_TEXT }
  | { payload: boolean; type: AppointmentActions.SET_IS_BINDING | AppointmentActions.SET_CREATE_NEW_WORKFLOW_INSTANCE }
  | { type: AppointmentActions.RESET };

export const AppointmentReducer = (
  state: AppointmentReducerState,
  action: AppointmentReducerAction,
): AppointmentReducerState => {
  switch (action.type) {
    case AppointmentActions.SET_WORK_ORDER:
      return { ...state, workOrder: action.payload };
    case AppointmentActions.SET_BRANCH:
      return { ...state, branch: action.payload };
    case AppointmentActions.SET_TEAMS:
      return { ...state, teams: action.payload };
    case AppointmentActions.SET_PEOPLE:
      return { ...state, people: action.payload };
    case AppointmentActions.SET_PARTNER:
      return { ...state, partner: action.payload };
    case AppointmentActions.SET_TYPE:
      return { ...state, appointmentType: action.payload ?? AppointmentTypeSelectionNullObject };
    case AppointmentActions.SET_START_DATE:
      if (state?.endDate && action.payload && state?.endDate < action.payload) {
        const newDate = new Date(action.payload);

        newDate.setHours(state.endDate.getHours());
        newDate.setMinutes(state.endDate.getMinutes());

        return { ...state, startDate: action.payload, endDate: newDate };
      }

      return { ...state, startDate: action.payload };
    case AppointmentActions.SET_END_DATE:
      return { ...state, endDate: action.payload };

    case AppointmentActions.SET_FROM_TIME:
      if (action.payload) {
        const [hours, minutes] = action.payload.split(':');
        const date = state.startDate ? new Date(state.startDate) : new Date();

        date.setHours(parseInt(hours));
        date.setMinutes(parseInt(minutes));

        return { ...state, startDate: date };
      }

      return state;
    case AppointmentActions.SET_TO_TIME:
      if (action.payload) {
        const [hours, minutes] = action.payload.split(':');
        const date = state.endDate ? new Date(state.endDate) : new Date(state.startDate ?? new Date());

        date.setHours(parseInt(hours));
        date.setMinutes(parseInt(minutes));

        return { ...state, endDate: date };
      }

      return state;
    case AppointmentActions.SET_NOTE_TEXT:
      return { ...state, note: action.payload };
    case AppointmentActions.SET_IS_BINDING:
      return { ...state, isBinding: action.payload };
    case AppointmentActions.SET_CREATE_NEW_WORKFLOW_INSTANCE:
      return { ...state, createNewWorkflowInstance: action.payload };
    case AppointmentActions.SET_APPOINTMENT:
      return action.payload;
    case AppointmentActions.RESET:
      return appointmentDefaultState;
    default:
      return state;
  }
};
